import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { getCountryName } from '../../utils/countries';
import { FileText, Download, Eye } from 'lucide-react';
import { formatBytes } from '../../context/DownloadContext';

const PUBLIC_BASE_URL = process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin;

export const buildQrUrl = (qrToken) => `${PUBLIC_BASE_URL}/public/qr/${qrToken}`;

const Section = ({ title, rows }) => (
  <div className="mb-4">
    <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2 print:text-black">{title}</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
      {rows.filter(([, v]) => v).map(([label, value], i) => (
        <div key={i} className="flex flex-col sm:flex-row">
          <span className="text-xs text-slate-500 sm:w-40 sm:shrink-0 print:text-gray-600">{label}:</span>
          <span className="text-xs text-slate-800 print:text-black break-words overflow-wrap-anywhere">{value}</span>
        </div>
      ))}
    </div>
  </div>
);

export const AirBarrierPrintView = ({ product, project, user, onDownloadSubmittal, onPreviewSubmittal, showActions = false }) => {
  if (!product) return null;

  const badge = (val, pos = ['Yes', 'Pass', 'Confirmed'], neg = ['No', 'Fail', 'Not confirmed']) => {
    if (!val) return null;
    const isPos = pos.includes(val);
    const isNeg = neg.includes(val);
    if (!isPos && !isNeg) return val;
    return <span className={`inline-flex items-center px-2 py-0.5 rounded ${isPos ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>{val}</span>;
  };
  const qrUrl = product.qrToken ? buildQrUrl(product.qrToken) : '';
  const alp = product.airLeakagePerformance || {};
  const cc = product.continuityComponents || {};
  const sc = product.substrateCompatibility || {};
  const ip = product.installationParameters || {};
  const fcc = product.fireCodeCompliance || {};
  const ir = product.interfaceResponsibility || {};
  const tv = product.testingVerification || {};
  const subs = product.submittals || [];
  const proj = project || product.project || {};
  const usr = user || product.viewedBy || {};

  return (
    <div className="print-view bg-white p-4 sm:p-6 max-w-3xl mx-auto" data-testid="air-barrier-print-view">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 pb-4 gap-3">
        <div className="flex-1">
          <h1 className="text-lg font-bold text-slate-900">{product.productNameModel}</h1>
          <p className="text-xs text-slate-500 mt-1">{product.manufacturerName || ''} {product.countryCode ? `| ${getCountryName(product.countryCode)}` : ''}</p>
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium mt-2 ${product.status === 'active' ? 'bg-green-600 text-white' : 'bg-amber-100 text-amber-700'}`}>{product.status || 'draft'}</span>
        </div>
        {qrUrl && (
          <div className="flex flex-col items-center ml-4 shrink-0" data-testid="print-qr-code">
            <QRCodeSVG value={qrUrl} size={80} level="L" />
            <p className="text-[8px] text-slate-400 mt-1 text-center max-w-[80px] break-all">Specard QR</p>
          </div>
        )}
      </div>

      <Section title="Project Information" rows={[
        ['Project Name', proj.name],
        ['Project Code', proj.projectCode],
        ['Address', proj.address],
        ['Owner', proj.owner],
      ]} />

      <Section title="Air Barrier Classification" rows={[
        ['Air Barrier Type', product.airBarrierType],
        ['Role of Product', product.roleOfProduct],
        ['Application Location', Array.isArray(product.applicationLocation) ? product.applicationLocation.join(', ') : product.applicationLocation],
      ]} />

      <Section title="Air Leakage Performance" rows={[
        ['Air Permeance Value', alp.airPermeanceValue],
        ['Test Standard', alp.testStandard],
        ['Test Pressure (Pa)', alp.testPressure],
        ['Compliance Statement', alp.complianceStatement],
      ]} />

      <Section title="Continuity & System Components" rows={[
        ['Required Accessories', Array.isArray(cc.requiredAccessories) ? cc.requiredAccessories.join(', ') : null],
        ['Compatible Products', cc.compatibleProducts],
        ['Continuity Method Description', cc.continuityMethodDescription],
        ['Discontinuity Risk Zones', cc.discontinuityRiskZones],
      ]} />

      <Section title="Substrate & Compatibility" rows={[
        ['Approved Substrates', sc.approvedSubstrates],
        ['Primer Required', sc.primerRequired],
        ['Compatibility with', sc.compatibilityWith],
      ]} />

      <Section title="Installation Parameters" rows={[
        ['Application Method', ip.applicationMethod === 'Other' ? `Other: ${ip.applicationMethodOther || ''}` : ip.applicationMethod],
        ['Wet Mil Thickness', ip.wetMilThickness],
        ['Cure Time', ip.cureTime],
        ['Installation Temperature Range (°F)', ip.installationTempRange],
        ['Max Exposure Before Covering (days)', ip.maxExposureDays],
      ]} />

      <Section title="Fire & Code Compliance" rows={[
        ['NFPA 285 Compatibility', badge(fcc.nfpa285Compatibility)],
        ['Referenced Wall Assemblies', fcc.referencedWallAssemblies],
        ['ASTM E84 (FSI / SDI)', fcc.astmE84],
        ['ICC-ES / Evaluation Report No', fcc.iccESReport],
      ]} />

      <Section title="Interface Responsibility" rows={[
        ['Interfaces with', ir.interfacesWith === 'Other' ? `Other: ${ir.interfacesWithOther || ''}` : ir.interfacesWith],
        ['Continuity Responsibility', ir.continuityResponsibility],
        ...(ir.continuityResponsibility === 'Other' ? [['Other Subcontractor', ir.otherSubcontractor]] : []),
        ['Transition Ownership', badge(ir.transitionOwnershipConfirmation ? 'Confirmed' : 'Not confirmed')],
      ]} />

      <Section title="Testing & Field Verification" rows={[
        ['Mock-up Required', badge(tv.mockupRequired)],
        ['Field Testing', Array.isArray(tv.fieldTesting) ? tv.fieldTesting.join(', ') : null],
        ['Test Agency', tv.testAgency],
        ['Pass / Fail Status', badge(tv.passFailStatus)],
        ['Test Result', tv.testResultSubmittal?.fileName || null],
      ]} />

      <Section title="Visual Coverage" rows={[
        ['Elevation PDF', product.visualCoverage?.elevationFileName || null],
        ['Markers', product.visualCoverage?.markers?.length ? `${product.visualCoverage.markers.length} marker(s)` : null],
      ]} />

      {subs.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2 print:text-black">Submittals ({subs.length})</h3>
          <div className="space-y-1.5">
            {subs.map((s, i) => (
              <div key={s.id || i} className="flex items-center gap-2 text-xs" data-testid={`print-submittal-${i}`}>
                <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0 print:text-gray-500" />
                <span className="text-slate-600 w-36 shrink-0">{s.documentType}</span>
                <span className="text-slate-800 flex-1 truncate">{s.fileName}</span>
                <span className="text-slate-400 shrink-0">{formatBytes(s.bytes)}</span>
                {showActions && onPreviewSubmittal && (
                  <button onClick={() => onPreviewSubmittal(s)} className="p-0.5 rounded text-slate-400 hover:bg-orange-500 hover:text-white transition-colors print:hidden" title="Preview" data-testid={`print-preview-${i}`}>
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                )}
                {showActions && onDownloadSubmittal && (
                  <button onClick={() => onDownloadSubmittal(s.id, s.fileName, s.bytes)} className="p-0.5 rounded text-slate-400 hover:bg-orange-500 hover:text-white transition-colors print:hidden" title="Download" data-testid={`print-download-${i}`}>
                    <Download className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border-t pt-3 mt-4 flex justify-between text-[10px] text-slate-400 print:text-gray-500">
        <span>Printed by: {usr.name || usr.email || 'Unknown'} ({usr.email || ''})</span>
        <span>{new Date().toLocaleString()}</span>
      </div>
    </div>
  );
};

export const QRThumbnail = ({ qrToken, size = 32 }) => {
  if (!qrToken) return <div className="bg-slate-100 rounded" style={{ width: size, height: size }} />;
  return <QRCodeSVG value={buildQrUrl(qrToken)} size={size} level="L" />;
};
